import { Badge, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconArrowLeft, IconLock } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { AssetImage } from '@/components/ui/AssetImage';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { ApiError } from '@/lib/api';
import { lockedCopy } from '@/features/content/accessCopy';
import { fetchTrainingMaterial, TRAINING_KEY } from '@/features/content/api';
import { TrainingBlock } from './TrainingBlocks';
import { LockedOffer } from '@/features/access/PlanOffer';

export function TrainingMaterialPage() {
  const { materialId = '' } = useParams();

  const query = useQuery({
    queryKey: [...TRAINING_KEY, materialId],
    queryFn: () => fetchTrainingMaterial(materialId),
    retry: false,
  });

  if (query.isLoading) return <LoadingState label="Открываем материал..." />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <>
        <PageHeader title="Материал" eyebrow="Обучение" />
        {notFound ? (
          <EmptyState
            title="Материал не найден"
            description="Возможно, ссылка устарела или материал сняли с публикации."
            action={
              <Button component={Link} to="/training" leftSection={<IconArrowLeft size={15} />}>
                К списку материалов
              </Button>
            }
          />
        ) : (
          <ErrorState onRetry={() => void query.refetch()} />
        )}
      </>
    );
  }

  const material = query.data!;
  const locked = lockedCopy(material.access);

  return (
    <>
      <PageHeader
        eyebrow="Обучение"
        title={material.title}
        description={material.description}
        actions={
          <Button
            component={Link}
            to="/training"
            variant="default"
            leftSection={<IconArrowLeft size={15} />}
          >
            Ко всем материалам
          </Button>
        }
      />

      {locked ? (
        // §19 — explain the lock calmly, no paywall modal.
        <Paper
          withBorder
          p="xl"
          radius="lg"
          style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-elevated)' }}
        >
          <Stack gap="md" align="center" ta="center">
            {material.coverImage ? (
              <div style={{ width: 220, filter: 'grayscale(0.7)', opacity: 0.5 }}>
                <AssetImage src={material.coverImage} alt="" ratio="16 / 9" />
              </div>
            ) : null}
            <Badge size="lg" color="gold" leftSection={<IconLock size={13} />}>
              {locked.reason}
            </Badge>
            {locked.hint ? (
              <Text size="sm" c="var(--text-muted)" maw={420}>
                {locked.hint}
              </Text>
            ) : null}
            <Text size="sm" c="var(--text-muted)" maw={460}>
              В материале {material.blockCount} блоков.
            </Text>
            {!material.access.allowed ? <LockedOffer access={material.access} content={{ kind: 'training_material', id: material.id }} /> : null}
          </Stack>
        </Paper>
      ) : (
        <Stack gap="lg">
          {material.coverImage ? (
            <Paper withBorder radius="lg" style={{ overflow: 'hidden', borderColor: 'var(--border-subtle)' }}>
              <AssetImage src={material.coverImage} alt={material.title} ratio="21 / 9" />
            </Paper>
          ) : null}

          {material.blocks.length === 0 ? (
            <EmptyState
              title="В материале пока нет блоков"
              description="Автор ещё наполняет материал."
            />
          ) : (
            material.blocks.map((block) => <TrainingBlock key={block.id} block={block} />)
          )}

          <Group gap="xs">
            <Text size="xs" c="var(--text-subtle)">
              Блоков: {material.blocks.length}
            </Text>
          </Group>
        </Stack>
      )}
    </>
  );
}
